import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Store } from "@ngrx/store";
import { AppState } from "@store/state";
import { ActionLoadCities } from "@store/store";
import { Store as TcStore, TcutsCity } from "@tendercuts/models";
import { Observable, Subscription } from "rxjs";
import { map, skipWhile, startWith, take } from "rxjs/operators";
import { BasePage } from "src/app/utils";

/**
 * The StoreFilterComponent allows users to filter and select stores based on cities and order types.
 * It fetches the cities and stores from the state and filters them based on user inputs.
 */
@Component({
  selector: "app-store-filter",
  templateUrl: "./store-filter.component.html",
  styleUrls: ["./store-filter.component.scss"],
})
export class StoreFilterComponent
  extends BasePage
  implements OnInit, OnDestroy, OnChanges
{
  @Output() triggerChange: EventEmitter<any> = new EventEmitter();

  @Input() city: string = "delhi";

  @Input() orderType: string = "2";

  sortedCity = [];

  selectedStoreData: string = "";

  storeSub$: Subscription;

  cities$: Subscription;

  filteredOptions: Observable<string[]>;

  myControl = new FormControl("");

  constructor(
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    public store: Store<AppState>
  ) {
    super(dialog, snackBar, store);
  }

  ngOnInit() {
    this.fetchCityBasedStores();
  }

  /**
   * Lifecycle hook: Called when an input property changes.
   * If the `orderType` changes, it will fetch the stores again.
   * @param changes Object containing the changed properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (typeof changes?.orderType !== "undefined") {
      this.fetchCityBasedStores();
      if (this.selectedStoreData) {
        this.myControl.setValue("");
        this.selectedStoreData = "";
      }
    }
  }

  /**
   * Filters the list of stores based on the user's input.
   * @param value The input value to filter by.
   * @returns An array of filtered stores.
   */
  private _filter(value: string | null): any[] {
    const filterValue = typeof value === "string" ? value?.toLowerCase() : "";

    return this.sortedCity.filter((option) =>
      option?.storeAttribute?.displayName?.toLowerCase()?.includes(filterValue)
    );
  }

  /**
   * Fetches the stores based on the selected city and order type by dispatching an action
   * and filtering the cities from the store.
   */
  async fetchCityBasedStores(): Promise<void> {
    await this.store.dispatch(new ActionLoadCities());
    let storesData = [];

    const cityData: Observable<TcutsCity[]> = await this.storeState.pipe(
      skipWhile((state) => state.cities.length == 0),
      map((state) => state.cities),
      take(1)
    );

    this.cities$ = cityData.subscribe((cities: TcutsCity[]) => {
      if (cities.length) {
        this.sortedCity = cities;
        this.sortedCity?.map((city) => {
          if (
            city?.stores?.length &&
            city?.name?.toLowerCase() == this.city?.toLowerCase()
          ) {
            let stores = city?.stores?.filter((store) => {
              return store.storeAttribute?.storeType == this.orderType;
            });
            storesData = [...storesData, ...stores];
          }
        });
        this.sortedCity = storesData;

        this.filteredOptions = this.myControl?.valueChanges?.pipe(
          startWith(""),
          map((value) => this._filter(value || ""))
        );

        return;
      }
    });
  }

  /**
   * Clears the current store filter and selected store data.
   */
  clearFilter(): void {
    this.myControl.setValue("");
    this.selectedStoreData = "";
    this.triggerChange.emit("");
  }

  /**
   * Handles store selection from the dropdown and emits the selected storeId.
   * @param event The selection event containing the selected store data.
   */
  onStoreSelect(event): void {
    let selectedValue: string = event?.option?.value?.storeId;
    this.selectedStoreData = selectedValue;
    this.triggerChange.emit(selectedValue);
  }

  /**
   * Displays the selected store name in the input field.
   * @param store The selected store object.
   * @returns The display name of the selected store.
   */
  displayFn(store: any): string {
    return store?.storeAttribute?.displayName;
  }

  /**
   * Lifecycle hook: Called when the component is destroyed.
   * It unsubscribes from any active subscriptions to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.storeSub$?.unsubscribe();
    this.cities$?.unsubscribe();
  }
}
